import React, { useState, useEffect, useRef } from 'react';
import { LABEL_CATALOG, ALL_LABELS, generateBrotherLabel, printLabel, downloadLabel, logPrint } from '../utils/brotherLabel';

const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

export default function PrintLabelModal({ unit, onClose }) {
  const [selectedSize, setSelectedSize] = useState('24x80');
  const previewRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!unit) return;
    const spec = ALL_LABELS.find(l => l.id === selectedSize);
    if (!spec) return;

    const canvas = generateBrotherLabel(unit, spec);
    canvasRef.current = canvas;
    const container = previewRef.current;
    if (container) {
      container.innerHTML = '';
      canvas.style.width = '100%';
      canvas.style.maxHeight = '220px';
      canvas.style.objectFit = 'contain';
      canvas.style.border = '1px solid #ddd';
      canvas.style.borderRadius = '4px';
      canvas.style.background = '#fff';
      container.appendChild(canvas);
    }
  }, [unit, selectedSize]);

  const filename = `label_${unit?.stockNumber || unit?.sku || unit?.id}_${selectedSize}.png`;

  const doPrint = () => {
    const canvas = canvasRef.current;
    if (!canvas) { alert('Label not ready yet.'); return; }
    const spec = ALL_LABELS.find(l => l.id === selectedSize);
    if (spec) logPrint(unit, spec);
    printLabel(canvas, spec);
  };

  const doDownload = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    downloadLabel(canvas, filename);
  };

  if (!unit) return null;
  const selectedSpec = ALL_LABELS.find(l => l.id === selectedSize);

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <h3 style={{ margin: 0, fontSize: '16px' }}>Print QR Code</h3>
          <span style={{ fontSize: '13px', color: '#666' }}>
            {unit.stockNumber || unit.sku || unit.id}
          </span>
          <button onClick={onClose} style={styles.closeBtn}>✕</button>
        </div>

        {/* Tape Size Selection */}
        <div style={styles.section}>
          <label style={styles.label}>Label Size</label>
          <div style={styles.sizeGrid}>
            {Object.entries(LABEL_CATALOG).map(([tapeWidth, group]) => (
              <div key={tapeWidth} style={styles.tapeGroup}>
                <div style={styles.tapeHeader}>{tapeWidth} tape</div>
                <div style={styles.tapeOptions}>
                  {group.labels.map(lbl => (
                    <button
                      key={lbl.id}
                      onClick={() => setSelectedSize(lbl.id)}
                      style={{
                        ...styles.sizeBtn,
                        ...(selectedSize === lbl.id ? styles.sizeBtnActive : {})
                      }}
                    >
                      {lbl.width}mm
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Preview */}
        <div style={styles.section}>
          <label style={styles.label}>
            Preview
            {selectedSpec && <span style={{ fontWeight: 'normal', color: '#888', marginLeft: 8 }}>
              {selectedSpec.px.w} × {selectedSpec.px.h}px
            </span>}
          </label>
          <div ref={previewRef} style={styles.preview} />
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 12, borderTop: '1px solid #eee' }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={doDownload} style={{ ...styles.secondaryBtn, flex: 1 }}>
              Save Image
            </button>
            <button onClick={doPrint} style={{ ...styles.primaryBtn, flex: 1 }}>
              Print
            </button>
          </div>
          {isMobile && (
            <div style={{ fontSize: 10, color: '#888', textAlign: 'center', lineHeight: 1.5 }}>
              Save the image, then open it in the <b>Brother iPrint&Label</b> app to print.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.5)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 9999
  },
  modal: {
    background: '#fff', borderRadius: '12px', width: '520px', maxWidth: '95vw',
    maxHeight: '90vh', overflow: 'auto', padding: '20px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
  },
  header: {
    display: 'flex', alignItems: 'center', gap: '12px',
    paddingBottom: '12px', borderBottom: '1px solid #eee', marginBottom: '16px'
  },
  closeBtn: {
    marginLeft: 'auto', background: 'none', border: 'none',
    fontSize: '18px', cursor: 'pointer', color: '#666'
  },
  section: { marginBottom: '16px' },
  label: { display: 'block', fontSize: '12px', fontWeight: '600', color: '#555', marginBottom: '6px', textTransform: 'uppercase' },
  sizeGrid: { display: 'flex', flexDirection: 'column', gap: '8px' },
  tapeGroup: { display: 'flex', alignItems: 'center', gap: '8px' },
  tapeHeader: { fontSize: '11px', fontWeight: '600', color: '#888', minWidth: '60px', textAlign: 'right' },
  tapeOptions: { display: 'flex', gap: '4px', flexWrap: 'wrap' },
  sizeBtn: {
    padding: '4px 10px', border: '1px solid #ddd', borderRadius: '4px',
    background: '#fafafa', cursor: 'pointer', fontSize: '12px'
  },
  sizeBtnActive: {
    background: '#e8f4fd', borderColor: '#2196F3', color: '#1565C0', fontWeight: '600'
  },
  preview: {
    background: '#f5f5f5', borderRadius: '8px', padding: '12px',
    minHeight: '80px', display: 'flex', alignItems: 'center', justifyContent: 'center'
  },
  primaryBtn: {
    padding: '14px 24px', border: 'none', borderRadius: '8px',
    background: '#1a1a2e', color: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: '600',
    textAlign: 'center'
  },
  secondaryBtn: {
    padding: '10px 18px', border: '1px solid #ddd', borderRadius: '8px',
    background: '#f8f8f8', cursor: 'pointer', fontSize: '13px', fontWeight: '500',
    textAlign: 'center'
  },
};
